/**
 * Shared domain types for the Exchange payment platform.
 * Consumed by api-server, trader-dashboard and admin-dashboard.
 *
 * Money convention: all fiat amounts are integer KOPECKS (1 RUB = 100).
 * All crypto amounts are integer MICRO-USDT (1 USDT = 1_000_000).
 * Never use floats for money.
 */

// ---------- Enums (kept as const objects so they survive `erasableSyntaxOnly`) ----------

export const KycStatus = {
  NotStarted: 'not_started',
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected',
} as const;
export type KycStatus = (typeof KycStatus)[keyof typeof KycStatus];

export const CardStatus = {
  Pending: 'pending',
  Verified: 'verified',
  Rejected: 'rejected',
} as const;
export type CardStatus = (typeof CardStatus)[keyof typeof CardStatus];

/**
 * Transaction lifecycle.
 *
 *   pending ──confirm──▶ confirmed ──settle──▶ settled
 *      │
 *      ├──deadline passes──▶ expired
 *      └──trader disputes──▶ disputed ──admin resolves──▶ settled | cancelled
 */
export const TxStatus = {
  Pending: 'pending',
  Confirmed: 'confirmed',
  Settled: 'settled',
  Expired: 'expired',
  Disputed: 'disputed',
  Cancelled: 'cancelled',
} as const;
export type TxStatus = (typeof TxStatus)[keyof typeof TxStatus];

export const DisputeReason = {
  NotReceived: 'not_received',
  WrongAmount: 'wrong_amount',
  Duplicate: 'duplicate',
  Other: 'other',
} as const;
export type DisputeReason = (typeof DisputeReason)[keyof typeof DisputeReason];

export const DisputeStatus = {
  Open: 'open',
  UnderReview: 'under_review',
  Resolved: 'resolved',
  Escalated: 'escalated',
} as const;
export type DisputeStatus = (typeof DisputeStatus)[keyof typeof DisputeStatus];

export const DisputeDecision = {
  /** Trader was right: payment never arrived. Transaction cancelled, no USDT released. */
  FavourTrader: 'favour_trader',
  /** Merchant was right: payment did arrive. Transaction settles, USDT released. */
  FavourMerchant: 'favour_merchant',
  /** Partial settlement at an admin-specified amount. */
  Partial: 'partial',
} as const;
export type DisputeDecision = (typeof DisputeDecision)[keyof typeof DisputeDecision];

export const AdminPermission = {
  KycReview: 'kyc_review',
  MerchantManage: 'merchant_manage',
  TransactionMonitor: 'transaction_monitor',
  DisputeResolve: 'dispute_resolve',
  AnalyticsView: 'analytics_view',
  SettingsManage: 'settings_manage',
} as const;
export type AdminPermission = (typeof AdminPermission)[keyof typeof AdminPermission];

export const ALL_ADMIN_PERMISSIONS: AdminPermission[] = Object.values(AdminPermission);

// ---------- Banks (for card registration; issuer BIN validation is separate) ----------

export const RU_BANKS = [
  'Sberbank',
  'Tinkoff',
  'Alfa-Bank',
  'VTB',
  'Raiffeisen',
  'Gazprombank',
  'Otkritie',
  'Rosbank',
  'Sovcombank',
  'MTS Bank',
  'Post Bank',
  'Ozon Bank',
  'Yandex Bank',
  'Душанбе Сити',
  'Other',
] as const;
export type RuBank = (typeof RU_BANKS)[number];

// ---------- Entities as seen by the API ----------

export interface TraderProfile {
  id: string;
  email: string;
  phone: string | null;
  twoFaEnabled: boolean;
  kycStatus: KycStatus;
  createdAt: string;
}

export interface KycSnapshot {
  status: KycStatus;
  passportUploaded: boolean;
  selfieUploaded: boolean;
  rejectionReason: string | null;
  reviewedAt: string | null;
  cards: BankCardSummary[];
  /** True when the trader may receive traffic: KYC approved AND >=1 verified card. */
  canReceiveTraffic: boolean;
}

export interface BankCardSummary {
  paymentDetailsReady?: boolean;
  id: string;
  last4: string;
  bank: string;
  holderName: string;
  status: CardStatus;
  isPrimary: boolean;
  createdAt: string;
}

export interface TransactionSummary {
  id: string;
  reference: string;
  merchantName: string;
  amountRub: number;
  amountUsdt: number;
  cardLast4: string | null;
  cardBank: string | null;
  status: TxStatus;
  confirmationDeadline: string;
  createdAt: string;
  confirmedAt: string | null;
  settledAt: string | null;
  /** Server-computed so the client never disagrees with the server about expiry. */
  secondsRemaining: number;
  hasOpenDispute: boolean;
}

export interface DisputeSummary {
  id: string;
  transactionId: string;
  transactionReference: string;
  traderEmail: string;
  merchantName: string;
  amountRub: number;
  reason: DisputeReason;
  status: DisputeStatus;
  statement: string;
  evidenceUrls: string[];
  decision: DisputeDecision | null;
  adminNotes: string | null;
  createdAt: string;
  resolvedAt: string | null;
  /** Automated risk signals surfaced to the reviewing admin. */
  riskFlags: RiskFlag[];
}

export interface RiskFlag {
  code:
    | 'repeat_disputer'
    | 'high_dispute_rate'
    | 'new_account'
    | 'large_amount'
    | 'rapid_disputes'
    | 'no_evidence';
  severity: 'low' | 'medium' | 'high';
  detail: string;
}

export interface MerchantSummary {
  id: string;
  companyName: string;
  contactEmail: string;
  webhookUrl: string | null;
  rateLimitPerMin: number;
  dailyLimitRub: number;
  isActive: boolean;
  createdAt: string;
  stats: {
    txCount24h: number;
    volumeRub24h: number;
    disputeRate: number;
  };
}

export interface AdminProfile {
  id: string;
  email: string;
  permissions: AdminPermission[];
  lastLoginAt: string | null;
}

// ---------- API envelopes ----------

export interface ApiError {
  error: string;
  code?: string;
  details?: unknown;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuthTokens {
  accessToken: string;
  expiresIn: number;
}

// ---------- Merchant-facing API (public contract) ----------

export interface CreatePaymentRequest {
  /** Merchant's own idempotency reference. Duplicate values return the original payment. */
  reference: string;
  amountRub: number;
  /** Optional: target a specific trader. Omit to let the platform route automatically. */
  traderId?: string;
  /** Overrides the merchant's default webhook for this payment only. */
  callbackUrl?: string;
}

export interface CreatePaymentResponse {
  platformFeeBps?:number; platformFeeUsdt?:number; merchantNetUsdt?:number;
  settledPlatformFeeUsdt?:number; settledMerchantNetUsdt?:number;
  rapiraAskRub?:number|null; rateQuotedAt?:string|null; rateSource?:string|null; traderMarkupBps?:number;
  paymentUrl?: string | null;
  qrPayload?: string | null;
  qrUrl?: string | null;
  deepLink?: string | null;
  settledAmountRub?: number;
  settledAmountUsdt?: number;
  paymentId: string;
  reference: string;
  amountRub: number;
  amountUsdt: number;
  status: TxStatus;
  /** Card the payer must send RUB to. */
  payTo: {
    bank: string;
    cardLast4: string;
    cardNumber?: string | null;
    holderName: string;
  };
  confirmationDeadline: string;
  createdAt: string;
}

export type WebhookEvent =
  | 'payment.pending'
  | 'payment.confirmed'
  | 'payment.settled'
  | 'payment.expired'
  | 'payment.disputed'
  | 'payment.cancelled';

export interface WebhookPayload {
  platformFeeUsdt?:number; merchantNetUsdt?:number;
  event: WebhookEvent;
  paymentId: string;
  reference: string;
  status: TxStatus;
  amountRub: number;
  amountUsdt: number;
  occurredAt: string;
  /** HMAC-SHA256 of the raw body using the merchant's webhook secret. */
  signature: string;
}

// ---------- Money helpers ----------

export const KOPECKS_PER_RUB = 100;
export const MICRO_PER_USDT = 1_000_000;

/**
 * The interface is Russian, so amounts follow Russian convention: a narrow
 * no-break space between thousands and a comma before the decimals
 * ("1 234 567,89"). Both apps render money through these two functions, so the
 * convention is defined once and cannot drift between screens.
 */
const MONEY_LOCALE = 'ru-RU';

/** 123456 kopecks -> "1 234,56" */
export function formatRub(kopecks: number): string {
  return (kopecks / KOPECKS_PER_RUB).toLocaleString(MONEY_LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** 1234560 micro-USDT -> "1,23" */
export function formatUsdt(micro: number): string {
  return (micro / MICRO_PER_USDT).toLocaleString(MONEY_LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Accepts what a Russian keyboard actually produces: "1 234,56", "1234.56",
 * a non-breaking or narrow space as the group separator, and either decimal
 * mark. Rejecting a comma here would mean a trader typing an amount the way
 * the interface displays it back to them gets NaN.
 */
export function parseRubToKopecks(rub: string | number): number {
  if (typeof rub === 'number') {
    return Math.round(rub * KOPECKS_PER_RUB);
  }
  const normalised = rub
    .replace(/[\s   ]/g, '')
    .replace(',', '.');
  const value = Number(normalised);
  return Number.isFinite(value) ? Math.round(value * KOPECKS_PER_RUB) : Number.NaN;
}
